import { useEffect, useState } from 'react';

/**
 * `true` cuando las webfonts críticas del Home ya están cargadas (o el
 * navegador no soporta la Font Loading API, o venció el timeout).
 *
 * Por qué existe: las fuentes llegan de Google Fonts con `display=swap` —
 * el texto pinta primero con la fuente fallback y se RE-MIDE cuando llega la
 * real. Si el reveal del Home corre antes del swap, las cards aparecen con
 * una altura y se encogen "un poquito" al instante (Geist tiene métricas más
 * compactas que el fallback) → reflow feo justo después de aparecer. Sumando
 * este flag al gate del hold-and-reveal, el contenido se mide UNA sola vez.
 *
 * `document.fonts.load()` además DISPARA la descarga de faces que todavía no
 * se usaron en pantalla (p.ej. Caveat, que recién aparece en el saludo del
 * reveal), así la espera y la descarga corren en paralelo durante el hold.
 *
 * Fail-open deliberado (default legítimo: es polish visual, no datos): si una
 * fuente nunca llega (CDN caído, offline), el timeout libera el reveal igual.
 */

// Una probe por familia usada above-the-fold en el Home (peso+tamaño reales).
const FONT_PROBES = ["700 17px Geist", '600 28px Caveat', "500 12px 'JetBrains Mono'"];
const FONTS_TIMEOUT_MS = 1200;

// Flag a nivel módulo: una vez asentadas, quedan asentadas para siempre (las
// fuentes no se "des-cargan") — los mounts siguientes devuelven true directo.
let settled = false;

export function useFontsSettled(): boolean {
  const [ready, setReady] = useState(settled);

  useEffect(() => {
    if (settled) return;
    if (typeof document === 'undefined' || !('fonts' in document)) {
      // Sin Font Loading API (jsdom/tests, browsers viejos) → no bloquear.
      settled = true;
      setReady(true);
      return;
    }
    let alive = true;
    const timeout = new Promise<void>((resolve) => {
      setTimeout(resolve, FONTS_TIMEOUT_MS);
    });
    const loads = Promise.all(FONT_PROBES.map((probe) => document.fonts.load(probe))).then(
      () => undefined,
      // Un face que falla no debe colgar el reveal (mismo espíritu que el timeout).
      () => undefined,
    );
    void Promise.race([loads, timeout]).then(() => {
      settled = true;
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  return ready;
}
